// LoadSection — body-only variant av LoadDialog. Listar sparade
// dokument från localStorage med ladda/delete/upload-repo-actions.
// Förberedande extract för #9 WorkspacePickerDialog-merge
// (POST_QA_SUBPLAN Wave E.2 step 2).
//
// Verbatim move: dokument-loop, loading-spinner, empty-state,
// load/delete-handlers — identiska med originalet. State
// (documents / loading / deletingId) stannar internt så host-
// dialogen inte behöver tråda igenom dem.
//
// Användning:
//   - Standalone-dialog: LoadDialog (legacy wrapper).
//   - Tabs-läge: blivande WorkspacePickerDialog.

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Pin, Loader2, FileText, ShieldCheck, FolderArchive } from 'lucide-react';
import { Document } from '@/types/document';
import { languageToLocale } from '@/lib/locale';
import { useTranslation } from '@/i18n/LanguageContext';

interface LoadSectionProps {
  /** Triggas när section:n monteras / open-state flippar till true. */
  open: boolean;
  /** Stänger värd-dialogen efter en lyckad load. */
  onClose: () => void;
  onList: () => Promise<Document[]>;
  onLoad: (id: string, title: string) => void;
  onDelete: (id: string) => Promise<void>;
  /** Öppna "Upload repository (.zip)"-dialogen. Section stänger
   *  värd-dialogen först om handlern är passad. */
  onOpenRepoUpload?: () => void;
}

export function LoadSection({
  open, onClose, onList, onLoad, onDelete, onOpenRepoUpload,
}: LoadSectionProps) {
  const { language } = useTranslation();
  const localeTag = languageToLocale(language);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      onList()
        .then(setDocuments)
        .catch(() => setDocuments([]))
        .finally(() => setLoading(false));
    }
  }, [open, onList]);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await onDelete(id);
      setDocuments(prev => prev.filter(d => d.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const handleLoad = (doc: Document) => {
    onLoad(doc.id, doc.title);
    onClose();
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(localeTag, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  return (
    <>
      {onOpenRepoUpload && (
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={() => { onClose(); onOpenRepoUpload(); }}
          data-testid="load-open-repo-upload"
        >
          <FolderArchive className="w-4 h-4" aria-hidden="true" />
          Ladda upp repository (.zip)
        </Button>
      )}

      <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && documents.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Inga sparade dokument hittades.</p>
          </div>
        )}

        {!loading && documents.length > 0 && (
          <div className="space-y-1">
            {documents.map((doc) => (
              <button
                key={doc.id}
                onClick={() => handleLoad(doc)}
                className={`w-full text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors flex items-start gap-3 group ${
                  doc.is_official
                    ? 'border-amber-400/60 bg-amber-50/30 dark:bg-amber-950/20'
                    : 'border-border'
                }`}
              >
                <FileText className={`w-5 h-5 mt-0.5 shrink-0 ${
                  doc.is_official ? 'text-amber-500' : 'text-muted-foreground'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{doc.title}</span>
                    {doc.is_official && (
                      <Badge className="bg-amber-500/90 text-white hover:bg-amber-500 text-[10px] px-1.5 py-0 gap-1">
                        <ShieldCheck className="w-3 h-3" />
                        Officiell
                      </Badge>
                    )}
                    {doc.is_pinned && <Pin className="w-3 h-3 text-primary shrink-0" />}
                  </div>
                  <div className="text-xs text-muted-foreground flex gap-3 mt-0.5">
                    <span>{doc.category}</span>
                    <span>Uppdaterad: {formatDate(doc.updated_at)}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0 opacity-0 group-hover:opacity-100 h-8 w-8 text-destructive hover:text-destructive"
                  onClick={(e) => handleDelete(e, doc.id)}
                  disabled={deletingId === doc.id}
                >
                  {deletingId === doc.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
